import { Injectable, NgZone, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';
import { ModalController } from '@ionic/angular/standalone';
import { environment } from '@infra-env/environment';
import { StorageKeys } from '@core-interfaces/global';
import { AppEvents } from '@core-interfaces/global/events';
import { IAppVersionCheckResponse } from '@core-ports/outputs/AppVersion';
import { AppVersionCheckService } from '@infra-adapters/services/app-version-check.service';
import { AnalyticsService } from '@infra-adapters/services/analytics.service';
import { AppVersionBlockModalComponent } from '@infra-ui/components/app-version-block-modal/app-version-block-modal.component';

const TTL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppVersionFacade {
  readonly blocking = signal(false);
  readonly result = signal<IAppVersionCheckResponse | null>(null);

  private modalOpen = false;
  private resumeListener: { remove: () => Promise<void> } | null = null;

  constructor(
    private checkService: AppVersionCheckService,
    private analytics: AnalyticsService,
    private modalController: ModalController,
    private ngZone: NgZone,
  ) {}

  start(): void {
    void this.verify(true);
    void this.listenResume();
  }

  async onUpdateTapped(): Promise<void> {
    const current = this.result();
    void this.analytics
      .logEvent(AppEvents.app_version_update_tapped, {
        platform: current?.platform,
        currentVersion: current?.currentVersion,
      })
      .catch(() => undefined);
    if (!current) return;
    try {
      const deepLink = current.storeDeepLink || '';
      const storeUrl = current.storeUrl || '';
      if (/^https?:\/\//i.test(deepLink)) {
        await Browser.open({ url: deepLink });
        return;
      }
      if (deepLink) {
        window.location.href = deepLink;
        window.setTimeout(() => {
          if (document.visibilityState === 'visible' && storeUrl) {
            void Browser.open({ url: storeUrl });
          }
        }, 700);
        return;
      }
      if (storeUrl) {
        await Browser.open({ url: storeUrl });
      }
    } catch {
      await Browser.open({ url: current.storeUrl });
    }
  }

  private async listenResume(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') return;
    if (this.resumeListener) return;
    this.resumeListener = await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        void this.verify(true);
      }
    });
  }

  private async verify(force = false): Promise<void> {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') return;
    if (platform !== 'ios' && platform !== 'android') return;

    let response: IAppVersionCheckResponse | null = null;
    try {
      if (!force) {
        const cached = await this.readCache();
        if (cached) {
          await this.apply(cached);
          return;
        }
      }
      response = await this.checkService.check(
        platform,
        environment.appVersion
      );
      await this.writeCache(response);
    } catch (err) {
      console.error('[app-version] check failed', err);
      return;
    }
    if (response) {
      await this.ngZone.run(() => this.apply(response));
    }
  }

  private async apply(response: IAppVersionCheckResponse): Promise<void> {
    this.result.set(response);
    if (response.degraded || !response.updateRequired) {
      this.blocking.set(false);
      if (this.modalOpen) {
        await this.modalController.dismiss();
        this.modalOpen = false;
      }
      return;
    }
    this.blocking.set(true);
    await this.presentModal(response);
  }

  private async presentModal(response: IAppVersionCheckResponse): Promise<void> {
    if (this.modalOpen) return;
    this.modalOpen = true;
    const modal = await this.modalController.create({
      component: AppVersionBlockModalComponent,
      componentProps: {
        currentVersion: response.currentVersion,
        minSupportedVersion: response.minSupportedVersion,
      },
      cssClass: 'app-version-block-modal',
      canDismiss: false,
      backdropDismiss: false,
      keyboardClose: false,
    });
    modal.onDidDismiss().then(() => {
      this.modalOpen = false;
    });
    try {
      await modal.present();
    } catch (err) {
      console.error('[app-version] modal present failed', err);
      this.modalOpen = false;
      return;
    }
    void this.analytics
      .logEvent(AppEvents.app_version_block_shown, {
        platform: response.platform,
        currentVersion: response.currentVersion,
        minSupportedVersion: response.minSupportedVersion ?? '',
      })
      .catch(() => undefined);
  }

  private async readCache(): Promise<IAppVersionCheckResponse | null> {
    const { value } = await Preferences.get({ key: StorageKeys.app_version_check });
    if (!value) return null;
    try {
      const parsed = JSON.parse(value) as { at: number; data: IAppVersionCheckResponse };
      if (Date.now() - parsed.at > TTL_MS) return null;
      return parsed.data;
    } catch {
      return null;
    }
  }

  private async writeCache(data: IAppVersionCheckResponse): Promise<void> {
    await Preferences.set({
      key: StorageKeys.app_version_check,
      value: JSON.stringify({ at: Date.now(), data }),
    });
  }
}
